import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { z } from 'zod'

import { get } from '../apiClient'
import { API_ENDPOINTS } from '../../../common/apiEndpoints'

const sharedAreaSchema = z.object({
  id: z.number(),
  realPropertyHierarchyId: z.number(),
  type: z.literal('gym'),
  buildingLocationDetails: z.string(),
  buildingPartLocationDetails: z.string(),
  locationDetails: z.string(),
  additionalDetails: z.string().nullable().optional(),
  residentDetailsFi: z.string().nullable(),
  residentDetailsSv: z.string().nullable(),
  residentDetailsEn: z.string().nullable(),
  totalSeats: z.number(),
  maxOwnSeats: z.number(),
  ownSeats: z.number(),
  availableSeats: z.number(),
  buildingPartAddress: z.object({
    streetName: z.string(),
    streetNumber: z.string(),
    city: z.string(),
  }),
  monthlyPrices: z.array(
    z.object({
      priceIncludingVat: z.number(),
      vatPercentage: z.number(),
      validFrom: z.string(),
      validTo: z.string(),
    })
  ),
  canReserve: z.boolean(),
})

const sharedAreasResponseSchema = z.array(sharedAreaSchema)

export type SharedArea = z.infer<typeof sharedAreaSchema>

const sharedAreasKey = (contractUuid: string, type: string) => [
  `${API_ENDPOINTS.CONTRACTS}/${contractUuid}/shared-areas`,
  type,
]

const path = (contractUuid: string, type: string) => {
  const startDate = DateTime.now().toISODate()
  return `/contracts/${contractUuid}/shared-areas?type=${type}&startDate=${startDate}`
}

const params = (contractUuid: string, type: string) => ({
  queryKey: sharedAreasKey(contractUuid, type),
  queryFn: async (): Promise<SharedArea[]> =>
    get(path(contractUuid, type), sharedAreasResponseSchema),
})

export const useAPIGetSharedAreas = (contractUuid: string, type: string) => {
  return useQuery(params(contractUuid, type))
}
